/**
 * Discoverability Provider
 *
 * App-wide host for contextual prompts and onboarding tours.
 *
 * Primary responsibilities:
 * - Load feature usage for the current user
 * - Enforce per-session prompt caps
 * - Bridge shared actions into the Onborda tour runtime
 *
 * Dependencies:
 * - Onborda
 * - Auth context
 */

'use client'

import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Onborda, OnbordaProvider, useOnborda } from 'onborda'

import { useAuth } from '@/context/auth'
import { useBetaFeatureAccess } from '@/hooks/use-beta-feature-access'
import { getEffectiveFeaturePlanId } from '@/lib/billing/losi-pricing'
import {
  DiscoverabilityContext,
  type DiscoverabilityContextValue,
  type DiscoverabilityEventInput,
  type DiscoverabilityPromptRequest,
  type DiscoverabilityTooltipOptions,
  type DiscoverabilityTourId
} from '@/lib/discoverability/context'
import type { DiscoverabilityTrackedFeatureKey } from '@/lib/discoverability/features'
import {
  getDiscoverabilityPrompts,
  DISCOVERABILITY_DISMISS_SNOOZE_DAYS,
  DISCOVERABILITY_MAX_SESSION_PROMPTS,
  isDiscoverabilityLocalDismissActive,
  type DiscoverabilityUsageSnapshot
} from '@/lib/discoverability/getDiscoverabilityPrompts'
import { getAccountAgeDays } from '@/lib/discoverability/journey'
import {
  createDiscoverabilityState,
  loadDiscoverabilityState,
  saveDiscoverabilityState,
  type DiscoverabilityState
} from '@/lib/discoverability/storage'
import { TourCard } from '@/components/onboarding/tour-card'
import { tourConfig } from '@/lib/onboarding/tour-config'
import { CustomDiscoverabilityLayer } from './custom-discoverability-layer'

const DEFAULT_TOUR_ID: DiscoverabilityTourId = 'main'
const DEFAULT_TOOLTIP_EXPIRE_DAYS = 14
const SESSION_PROMPT_COUNT_KEY = 'losi:discoverability:session-prompt-count'

const createTimestamp = () => new Date().toISOString()

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const isTextEntryActive = () => {
  if (typeof document === 'undefined') return false

  const element = document.activeElement
  if (!element) return false

  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element.getAttribute('contenteditable') === 'true'
  )
}

const isExpired = (timestamp: string, expireDays: number) => {
  const lastSeenAt = new Date(timestamp).getTime()

  if (!Number.isFinite(lastSeenAt)) {
    return true
  }

  return Date.now() > lastSeenAt + expireDays * 24 * 60 * 60 * 1000
}

const getSessionPromptCount = (userId?: string | null) => {
  if (typeof window === 'undefined') return 0

  const value = window.sessionStorage.getItem(`${SESSION_PROMPT_COUNT_KEY}:${userId || 'guest'}`)
  const count = Number.parseInt(value || '0', 10)

  return Number.isFinite(count) && count > 0 ? count : 0
}

const saveSessionPromptCount = (userId: string | null | undefined, count: number) => {
  if (typeof window === 'undefined') return

  window.sessionStorage.setItem(
    `${SESSION_PROMPT_COUNT_KEY}:${userId || 'guest'}`,
    String(Math.min(count, DISCOVERABILITY_MAX_SESSION_PROMPTS))
  )
}

const toUsageSnapshotMap = (
  value: unknown
): Partial<Record<DiscoverabilityTrackedFeatureKey, DiscoverabilityUsageSnapshot>> => {
  if (!isRecord(value)) return {}

  return value as Partial<Record<DiscoverabilityTrackedFeatureKey, DiscoverabilityUsageSnapshot>>
}

export function DiscoverabilityProvider({ children }: { children: ReactNode }) {
  return (
    <OnbordaProvider>
      <DiscoverabilityController>{children}</DiscoverabilityController>
    </OnbordaProvider>
  )
}

function DiscoverabilityController({ children }: { children: ReactNode }) {
  const { profile, user } = useAuth()
  const { features: enabledBetaFeatures } = useBetaFeatureAccess()
  const onborda = useOnborda()
  const [state, setState] = useState<DiscoverabilityState>(createDiscoverabilityState)
  const [usage, setUsage] = useState<
    Partial<Record<DiscoverabilityTrackedFeatureKey, DiscoverabilityUsageSnapshot>>
  >({})
  const [sessionPromptCount, setSessionPromptCount] = useState(0)
  const [ready, setReady] = useState(false)
  const userId = user?.id ?? null
  const userPlan = profile ? getEffectiveFeaturePlanId(profile) : 'free'
  const accountAgeDays = getAccountAgeDays(
    (profile as { created_at?: string | null } | null)?.created_at ?? user?.created_at ?? null
  )

  useEffect(() => {
    let disposed = false

    setReady(false)
    setState(loadDiscoverabilityState(userId))
    setSessionPromptCount(getSessionPromptCount(userId))

    if (!userId) {
      setUsage({})
      setReady(true)
      return
    }

    fetch('/api/discoverability/usage', {
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to load discoverability usage')
        }

        const payload: unknown = await response.json()
        const usagePayload = isRecord(payload) ? payload.usage : null

        if (!disposed) {
          setUsage(toUsageSnapshotMap(usagePayload))
        }
      })
      .catch(() => {
        if (!disposed) {
          setUsage({})
        }
      })
      .finally(() => {
        if (!disposed) {
          setReady(true)
        }
      })

    return () => {
      disposed = true
    }
  }, [userId])

  useEffect(() => {
    saveSessionPromptCount(userId, sessionPromptCount)
  }, [sessionPromptCount, userId])

  useEffect(() => {
    if (!ready) return

    saveDiscoverabilityState(userId, state)
  }, [ready, state, userId])

  const refreshUsage = useCallback(async () => {
    if (!userId) return

    const response = await fetch('/api/discoverability/usage', {
      cache: 'no-store',
      credentials: 'same-origin'
    })

    if (!response.ok) return

    const payload: unknown = await response.json()
    const usagePayload = isRecord(payload) ? payload.usage : null
    setUsage(toUsageSnapshotMap(usagePayload))
  }, [userId])

  const mergeUsageSnapshot = useCallback((snapshot: DiscoverabilityUsageSnapshot | null) => {
    if (!snapshot) return

    setUsage((current) => ({
      ...current,
      [snapshot.featureKey]: snapshot
    }))
  }, [])

  const countPromptImpression = useCallback(() => {
    setSessionPromptCount((current) => Math.min(current + 1, DISCOVERABILITY_MAX_SESSION_PROMPTS))
  }, [])

  const updateState = useCallback(
    (mutate: (current: DiscoverabilityState) => DiscoverabilityState) => {
      setState((current) => mutate(current))
    },
    []
  )

  const markTooltipSeen = useCallback(
    (featureId: string) => {
      updateState((current) => ({
        ...current,
        seenTooltips: {
          ...current.seenTooltips,
          [featureId]: createTimestamp()
        }
      }))
    },
    [updateState]
  )

  const shouldShowTooltip = useCallback(
    (featureId: string, options: DiscoverabilityTooltipOptions = {}) => {
      if (!ready) return false
      if (options.forceShow) return true

      const lastSeenAt = state.seenTooltips[featureId]
      if (!lastSeenAt) return true

      return isExpired(lastSeenAt, options.expireDays ?? DEFAULT_TOOLTIP_EXPIRE_DAYS)
    },
    [ready, state.seenTooltips]
  )

  const isPromptDismissed = useCallback(
    (promptId: string, snoozeDays: number = DISCOVERABILITY_DISMISS_SNOOZE_DAYS) => {
      if (!ready) return false
      return isDiscoverabilityLocalDismissActive(state.dismissedPrompts[promptId], snoozeDays)
    },
    [ready, state.dismissedPrompts]
  )

  const dismissPrompt = useCallback(
    (promptId: string) => {
      updateState((current) => ({
        ...current,
        dismissedPrompts: {
          ...current.dismissedPrompts,
          [promptId]: createTimestamp()
        }
      }))
    },
    [updateState]
  )

  const trigger = useCallback(
    (triggerId: string) => {
      updateState((current) => ({
        ...current,
        triggerCounts: {
          ...current.triggerCounts,
          [triggerId]: (current.triggerCounts[triggerId] ?? 0) + 1
        },
        lastTriggeredAt: {
          ...current.lastTriggeredAt,
          [triggerId]: createTimestamp()
        }
      }))
    },
    [updateState]
  )

  const startTour = useCallback(
    (tourId: DiscoverabilityTourId = DEFAULT_TOUR_ID) => {
      onborda.startOnborda(tourId)
      trigger(`tour:${tourId}:started`)
    },
    [onborda, trigger]
  )

  const completeTour = useCallback(
    (tourId: DiscoverabilityTourId = DEFAULT_TOUR_ID) => {
      onborda.closeOnborda()
      updateState((current) => ({
        ...current,
        completedTours: {
          ...current.completedTours,
          [tourId]: createTimestamp()
        }
      }))
    },
    [onborda, updateState]
  )

  const getPrompt = useCallback(
    (request: DiscoverabilityPromptRequest) => {
      if (isTextEntryActive()) return null

      const prompts = getDiscoverabilityPrompts({
        ...request,
        usage,
        sessionPromptCount,
        userPlan,
        enabledBetaFeatures,
        accountAgeDays,
        hasActivePrompt: onborda.isOnbordaVisible
      })

      return prompts[0] ?? null
    },
    [
      accountAgeDays,
      enabledBetaFeatures,
      onborda.isOnbordaVisible,
      sessionPromptCount,
      usage,
      userPlan
    ]
  )

  const markFeatureUsed = useCallback(
    async (input: {
      featureKey: DiscoverabilityTrackedFeatureKey
      surface?: string | null
      trigger?: string | null
      metadata?: unknown
    }) => {
      if (!userId) return

      const response = await fetch('/api/discoverability/usage', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      })

      if (!response.ok) return

      const payload: unknown = await response.json()
      const snapshot = isRecord(payload) ? payload.usage : null

      mergeUsageSnapshot(isRecord(snapshot) ? (snapshot as DiscoverabilityUsageSnapshot) : null)
    },
    [mergeUsageSnapshot, userId]
  )

  const recordEvent = useCallback(
    async (input: DiscoverabilityEventInput) => {
      if (!userId) return

      // Tooltips must not burn the journey card session budget.
      if (input.eventType === 'prompt_shown') {
        countPromptImpression()
      }

      const response = await fetch('/api/discoverability/events', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      })

      if (response.ok) {
        await refreshUsage()
      }
    },
    [countPromptImpression, refreshUsage, userId]
  )

  const completeFeatureOnboarding = useCallback(
    async (featureKey: DiscoverabilityTrackedFeatureKey) => {
      await recordEvent({
        featureKey,
        eventType: 'onboarding_completed',
        trigger: 'onboarding'
      })
    },
    [recordEvent]
  )

  const value = useMemo<DiscoverabilityContextValue>(
    () => ({
      ready,
      state,
      usage,
      sessionPromptCount,
      userPlan,
      enabledBetaFeatures,
      activeTourId: onborda.currentTour,
      isTourActive: onborda.isOnbordaVisible,
      shouldShowTooltip,
      markTooltipSeen,
      isPromptDismissed,
      dismissPrompt,
      getPrompt,
      markFeatureUsed,
      recordEvent,
      completeFeatureOnboarding,
      trigger,
      startTour,
      completeTour
    }),
    [
      completeFeatureOnboarding,
      completeTour,
      dismissPrompt,
      enabledBetaFeatures,
      getPrompt,
      isPromptDismissed,
      markTooltipSeen,
      markFeatureUsed,
      onborda.currentTour,
      onborda.isOnbordaVisible,
      ready,
      recordEvent,
      sessionPromptCount,
      shouldShowTooltip,
      startTour,
      state,
      trigger,
      usage,
      userPlan
    ]
  )

  return (
    <DiscoverabilityContext.Provider value={value}>
      <Onborda
        steps={tourConfig}
        shadowRgb="17, 24, 39"
        shadowOpacity="0.58"
        cardComponent={TourCard}
        cardTransition={{ duration: 0.24, type: 'tween' }}
      >
        {children}
        <CustomDiscoverabilityLayer />
      </Onborda>
    </DiscoverabilityContext.Provider>
  )
}
